import React from "react";
import PropTypes from "prop-types";
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from "@material-ui/core";
import { makeStyles, createStyles, withStyles } from "@material-ui/core/styles";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Tooltip from "@material-ui/core/Tooltip";
import * as H from "helper";

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      borderRadius: 8,
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
    },
    content: {
      padding: theme.spacing(1),
      "&:last-child": {
        paddingBottom: theme.spacing(1),
      },
    },
    row: {
      alignItems: "stretch",
    },
    item: {
      padding: theme.spacing(1.25, 2),
      minHeight: 92,
      borderRight: "1px solid #e5e7eb",
      "&:last-child": {
        borderRight: "none",
      },
      [theme.breakpoints.down("sm")]: {
        borderRight: "none",
        borderBottom: "1px solid #e5e7eb",
        "&:last-child": {
          borderBottom: "none",
        },
      },
    },
    labelRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(0.75),
      minHeight: 24,
    },
    label: {
      color: "#6b7280",
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.2,
    },
    helpIcon: {
      fontSize: 16,
      color: "#9ca3af",
      flexShrink: 0,
    },
    value: {
      paddingTop: 0,
      paddingBottom: 0,
      minWidth: 150,
      whiteSpace: "pre-line",
    },
    loadingValue: {
      minHeight: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    sub: {
      color: "#94a3b8",
      fontSize: 12,
      lineHeight: 1.4,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      marginTop: theme.spacing(0.75),
      padding: "3px 8px",
      borderRadius: 999,
      background: "#d1fae5",
      color: "#065f46",
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
  })
);

const TooltipIcon = withStyles({
  tooltip: {
    fontSize: 12,
  },
})(Tooltip);

const PlotSummary = ({ items, loading }) => {
  const classes = useStyles();

  const formatValue = (value) =>
    typeof value === "number" ? H.FormatNumStrHelper.formatCommaThousand(value) : value;

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <Grid container spacing={0} className={classes.row}>
          {items.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={item.label}>
              <div className={classes.item}>
                <div className={classes.labelRow}>
                  <Typography  className={classes.label}>{item.label}</Typography>
                  {item.tooltip ? (
                    <TooltipIcon title={item.tooltip} arrow>
                      <HelpOutlineIcon className={classes.helpIcon} />
                    </TooltipIcon>
                  ) : null}
                </div>
                {/* <div className={classes.value}>{formatValue(item.value)}</div> */}
                {loading ? (
                  <Box className={classes.loadingValue}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Typography variant="h6" className={classes.value}>
                    {formatValue(item.value)}
                  </Typography>
                )}
                <div className={classes.sub}>{item.sub}</div>
                {item.badge ? <div className={classes.badge}>{item.badge}</div> : null}
              </div>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

PlotSummary.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      sub: PropTypes.string,
      badge: PropTypes.string,
      tooltip: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool,
};

PlotSummary.defaultProps = {
  loading: false,
};

export default PlotSummary;
